/**
 * WhatsAppButton — Click-to-Chat (wa.me) con el número del cliente.
 * Abre WhatsApp Web / la app con el mensaje ya escrito (borrador de la IA) para
 * que la agente lo envíe desde el número de empresa. Con `showLog`, ofrece
 * registrar el envío EN ZICASSO como "SMS/WhatsApp enviado" (interno) mediante
 * el mismo diálogo que Zicasso, avanzando el recordatorio.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { htmlToText } from "@/lib/html";
import { LogActivityDialog } from "@/components/LogActivityDialog";

const toDigits = (prefix, number) => `${prefix ?? ""}${number ?? ""}`.replace(/\D/g, "");
const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const toParagraphs = (txt) =>
  (txt || "").split("\n").map((l) => (l.trim() ? `<p>${escapeHtml(l)}</p>` : "<p><br></p>")).join("");

export function WhatsAppButton({ lead, text = "", showLog = true, size = "sm", onLogged }) {
  const digits = toDigits(lead?.traveler_phone_prefix, lead?.traveler_phone_number);
  const plain = htmlToText(text).trim();
  const [logOpen, setLogOpen] = useState(false);

  const openWa = () => {
    if (!digits) {
      toast.error("Este lead no tiene teléfono guardado");
      return;
    }
    const url = `https://wa.me/${digits}${plain ? `?text=${encodeURIComponent(plain)}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size={size}
        onClick={openWa}
        disabled={!digits}
        className="bg-[#25D366] text-white hover:bg-[#20bd5a] disabled:opacity-50"
        data-testid="whatsapp-open-button"
        title={digits ? "Abrir chat de WhatsApp con el cliente" : "Sin teléfono guardado"}
      >
        <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
      </Button>
      {showLog && (
        <Button
          type="button"
          size={size}
          variant="outline"
          onClick={() => setLogOpen(true)}
          disabled={!lead?.id}
          data-testid="whatsapp-log-button"
          title="Registrar en Zicasso que has enviado este mensaje por WhatsApp"
        >
          <Check className="mr-1.5 h-3.5 w-3.5" />
          Registrar envío en Zicasso
        </Button>
      )}
      {showLog && (
        <LogActivityDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          lead={lead}
          defaultKind="text"
          initialNotesHtml={plain ? toParagraphs(plain) : ""}
          onLogged={onLogged}
        />
      )}
    </div>
  );
}

export default WhatsAppButton;
